import Router from 'next/router';
import { proxy, subscribe, useSnapshot } from 'valtio';
import { derive } from 'valtio/utils';

import { GenreNameToKey, parseGenreCaseInsensitive } from '@soundxyz/utils/const';
import {
  REFERRAL_ADDRESS_SEARCH_PARAM,
  REFERRAL_SOURCE_SEARCH_PARAM,
} from '@soundxyz/utils/constants/embeds';
import { useSafeLayoutEffect } from '@components-shared/src/hooks/useSafeLayoutEffect';
import { createEnum } from '@utils/enums';

import type { EnumValueType } from '@utils/enums';
const ParamsValues = [
  REFERRAL_ADDRESS_SEARCH_PARAM,
  REFERRAL_SOURCE_SEARCH_PARAM,
  'playlistFilter',
  'sort',
  'tab',
  'type',
  'timespan',
  'initialRelease',
  'duplicatePlaylist',
  'code',
  'q',
  'socials',
  'state',
  'genre',
  'search_genre',
  'search_location_label',
  'search_location_place_id',
  'search_availability',
  'explore_type',
  'feature_gates',
  'mint_start_time',
  'total_minted',
  'created_at',
  'num_referral_purchases',
  'total_volume',
  'artists_backed',
  'song_tab',
  'nfts_count',
  'first_nft_collected_date',
] as const;

export const Params = createEnum(ParamsValues);
export type Params = EnumValueType<typeof Params>;

export type NullishRecord<T extends Record<string, unknown>> = {
  [SubKey in keyof T]?: T[SubKey] | null;
};

export type ParamsValues = NullishRecord<Record<Params, string>>;

export const ParamsStore = proxy<Record<Params, string | null>>({
  referral: null,
  referral_source: null,
  playlistFilter: null,
  sort: null,
  tab: null,
  type: null,
  timespan: null,
  initialRelease: null,
  duplicatePlaylist: null,
  code: null,
  q: null,
  socials: null,
  state: null,
  genre: null,
  search_genre: null,
  search_location_label: null,
  search_location_place_id: null,
  search_availability: null,
  explore_type: null,
  feature_gates: null,
  mint_start_time: null,
  total_minted: null,
  created_at: null,
  num_referral_purchases: null,
  total_volume: null,
  artists_backed: null,
  song_tab: null,
  nfts_count: null,
  first_nft_collected_date: null,
});

updateParams();

if (typeof window !== 'undefined') {
  subscribe(ParamsStore, ops => {
    const url = new URL(window.location.href);

    const newUrl = new URL(url);

    for (const paramType of ParamsValues) {
      const storeValue = ParamsStore[paramType];

      if (storeValue) {
        newUrl.searchParams.set(paramType, storeValue);
      } else {
        newUrl.searchParams.delete(paramType);
      }
    }

    if (newUrl.href !== url.href) {
      if (ops.some(([operation, path]) => operation === 'set' && path[0] === Params.explore_type)) {
        Router.push(newUrl, undefined, {
          shallow: true,
        });
      } else {
        history.replaceState(null, Router.pathname, newUrl);
      }
    }
  });
}

function updateParams() {
  const locationHref = getWindowLocationHref();
  if (!locationHref) return;

  const url = new URL(locationHref);

  for (const paramType of ParamsValues) {
    const value = url.searchParams.get(paramType);

    if (ParamsStore[paramType] !== value) {
      ParamsStore[paramType] = value;
    }
  }
}

export function useUpdateParams() {
  const locationHref = getWindowLocationHref();
  useSafeLayoutEffect(updateParams, [locationHref]);
}

export function getWindowLocationHref() {
  return typeof window === 'undefined' ? null : window.location.href;
}

const transformedStore = derive(
  {
    genreKey(get) {
      const genre = get(ParamsStore).genre || get(ParamsStore).search_genre;

      if (!genre) return null;

      const parsedGenre = parseGenreCaseInsensitive(decodeURIComponent(genre));

      if (!parsedGenre) return null;

      return GenreNameToKey[parsedGenre];
    },
  },
  {
    proxy: ParamsStore,
  },
);

export function useParams() {
  return useSnapshot(transformedStore);
}
